"use client";

// Third-party Imports
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

// Type Imports
import type { ElearningTypes, GetLessonParams, UpdateVideoProgressParams, SaveQuestionAnswersParams } from "@/types/apps/elearningTypes";

// Action Imports
import {
  doGetElearningCourses,
  doGetElearningCourseLessonById,
  doUpdateVideoProgress,
  doSaveElearningQuestionAnswers,
  doGetElearningCourseTeamProgress,
} from "@/app/server/actions";

const initialState: ElearningTypes = {
  courses: [],
  filteredCourses: [],
  lesson: {
    data: null,
    loading: "idle",
    error: null,
  },
  videoProgress: {
    loading: "idle",
    error: null,
  },
  teamProgress: {
    data: null,
    loading: "idle",
    error: null,
  },
  loading: "idle",
  error: null,
  total: 0,
  lastErrorToastTime: 0,
} as any; // allow internal cache props

export const elearningSlice = createSlice({
  name: "elearning",
  initialState: initialState,
  reducers: {
    // Clear current lesson data to avoid stale renders between lesson switches
    clearLesson: (state: ElearningTypes | any) => {
      state.lesson = {
        data: null,
        loading: "idle", // Set to idle instead of pending to allow refetch
        error: null,
      } as any;

      // Keep answersCache across lesson navigation so prefilled answers survive
    },
    updateVideoProgressOptimistic: (
      state: ElearningTypes | any,
      action: PayloadAction<{ lessonItemId: string; progress: number; completedAt?: string | null }>
    ) => {
      const { lessonItemId, progress, completedAt } = action.payload;

      if (state.lesson.data && state.lesson.data.lesson_data) {
        const itemIndex = state.lesson.data.lesson_data.findIndex((item: any) => {
          const itemId = typeof item._id === "object" ? item._id.$oid : item._id;


          return itemId === lessonItemId;
        });

        if (itemIndex !== -1) {
          // Create a new object to ensure React detects the change
          state.lesson.data.lesson_data[itemIndex] = {
            ...state.lesson.data.lesson_data[itemIndex],
            user_data: {
              ...state.lesson.data.lesson_data[itemIndex].user_data,
              progress: progress || state.lesson.data.lesson_data[itemIndex].user_data?.progress,
              completedAt: completedAt || state.lesson.data.lesson_data[itemIndex].user_data?.completedAt,
            },
          };
        }
      }
    },

    // Reset video progress error
    clearVideoProgressError: (state: ElearningTypes | any) => {
      state.videoProgress.error = null;
    },

    // Optimistically update question answers in lesson_data.user_data.content
    updateQuestionAnswersOptimistic: (state: ElearningTypes | any, action: PayloadAction<{ answers: Array<{ dataId?: string; answer: string }> }>) => {
      const { answers } = action.payload;

      if (!state.lesson.data || !Array.isArray(state.lesson.data.lesson_data)) return;
      if (!Array.isArray(answers) || answers.length === 0) return;

      answers.forEach(a => {
        const dataId = a?.dataId;

        if (!dataId) return;

        const ld = state.lesson.data!.lesson_data.find((item: any) => {
          const id = typeof item._id === "object" ? item._id.$oid : item._id;


          return id === dataId;
        });

        if (ld) {
          const current = (ld as any).user_data || {};


          // Only update if we have actual content to save (don't overwrite with empty strings)
          if (a.answer !== undefined) {
            (ld as any).user_data = {
              ...current,
              content: a.answer,
            };
          }
        }
      });
    },

    // Cache latest answers locally to survive navigation without refetch
    cacheQuestionAnswers: (state: ElearningTypes | any, action: any) => {
      const { answers } = action.payload as { answers: Array<{ dataId?: string; answer: string }> };

      if (!Array.isArray(answers) || answers.length === 0) return;
      if (!(state as any).answersCache) (state as any).answersCache = {};
      answers.forEach(a => {
        if (a?.dataId) (state as any).answersCache[a.dataId] = a.answer;
      });
    },

    // Clear the answers cache (call on course reset to prevent stale prefill)
    clearAnswersCache: (state: ElearningTypes | any) => {
      (state as any).answersCache = {};
    },

    // Filter courses into filteredCourses using search text and hideCompleted flag
    filterCourses: (
      state: ElearningTypes | any,
      action: PayloadAction<{ search?: string; hideCompleted?: boolean; categoryId?: string | "All"; showTeamOwnerOnly?: boolean }>
    ) => {
      const search = (action.payload?.search || "").toLowerCase();
      const hideCompleted = action.payload?.hideCompleted ?? false;
      const categoryId = action.payload?.categoryId;
      const showTeamOwnerOnly = action.payload?.showTeamOwnerOnly ?? false;
      const base: any[] = Array.isArray(state.courses) ? state.courses : [];
      let filtered = base;

      if (search) {
        filtered = filtered.filter((c: any) =>
          String(c?.name || "")
            .toLowerCase()
            .includes(search)
        );
      }

      if (hideCompleted) {
        filtered = filtered.filter((c: any) => {
          const completedFlag = Boolean(c?.completed);
          const progress = Number((c as any)?.overall_progress ?? 0) || 0;

          // Fallback: if backend 'completed' flag is missing/outdated, derive from lessons.lesson_progress
          let lessonsAllDone = false;

          try {
            const lessons: any[] = Array.isArray((c as any)?.lessons) ? ((c as any)?.lessons as any[]) : [];

            if (lessons.length > 0) {
              lessonsAllDone = lessons.every(l => (Number((l as any)?.lesson_progress ?? 0) || 0) >= 99.5);
            }
          } catch { }

          const effectivelyCompleted = completedFlag || progress >= 99.5 || lessonsAllDone;


          return !effectivelyCompleted;
        });
      }

      if (categoryId && categoryId !== "All") {
        filtered = filtered.filter((c: any) => {
          const cid = typeof c?.categoryId === "object" ? c?.categoryId?.$oid || c?.categoryId?.oid : c?.categoryId;


          return String(cid) === String(categoryId);
        });
      }

      if (showTeamOwnerOnly) {
        // Filter will be applied in the component using userTeams from Redux
        // For now, just mark that team filter is active
        (state as any).teamOwnerFilterActive = true;
      } else {
        (state as any).teamOwnerFilterActive = false;
      }

      state.filteredCourses = filtered;
      state.total = filtered.length;
    },
  },
  extraReducers: builder => {
    // Handle getCourses actions
    builder.addCase(getCourses.pending, state => {
      state.loading = "pending";
    });

    builder.addCase(getCourses.fulfilled, (state, action) => {
      state.loading = "succeeded";
      state.courses = action.payload.data || [];
      state.filteredCourses = action.payload.data || [];
      state.total = action.payload.data?.length || 0;
    });

    builder.addCase(getCourses.rejected, (state, action) => {
      // Don't show error if the request was aborted (React Strict Mode or component unmount)
      if (action.meta.aborted) {
        state.loading = "idle";
        return;
      }

      state.loading = "failed";
      state.error = (action.payload as string) || "Failed to load courses";

      // Only show toast if there's an actual error and enough time has passed since last toast
      // This prevents duplicates in React Strict Mode (500ms debounce)
      const now = Date.now();
      const timeSinceLastToast = now - ((state as any).lastErrorToastTime || 0);

      if (action.payload && timeSinceLastToast > 500) {
        (state as any).lastErrorToastTime = now;
        toast.error("Failed to load courses. Please try again.", {
          toastId: "courses-load-error",
        });
      }
    });

    // Handle getLesson actions
    builder.addCase(getLesson.pending, state => {
      // Don't clear lesson data to preserve optimistic updates
      state.lesson.loading = "pending";
      state.lesson.error = null;
    });

    builder.addCase(getLesson.fulfilled, (state, action) => {
      state.lesson.loading = "succeeded";

      // Merge server data with any optimistic updates
      const serverData = action.payload.data;
      const existingData = state.lesson.data;

      // Only merge if it's the SAME lesson (check lesson IDs match)
      const serverLessonId = serverData?._id ? (typeof serverData._id === "object" ? serverData._id.$oid : serverData._id) : null;
      const existingLessonId = existingData?._id ? (typeof existingData._id === "object" ? existingData._id.$oid : existingData._id) : null;

      if (existingData && existingData.lesson_data && serverData && serverData.lesson_data && serverLessonId === existingLessonId) {
        // Same lesson: Preserve optimistic updates by merging user_data
        serverData.lesson_data = serverData.lesson_data.map((serverItem: any) => {
          const serverId = typeof serverItem._id === "object" ? serverItem._id.$oid : serverItem._id;

          const existingItem = existingData.lesson_data.find((item: any) => {
            const existingId = typeof item._id === "object" ? item._id.$oid : item._id;


            return existingId === serverId;
          });

          if (existingItem && existingItem.user_data) {
            // Merge: prefer optimistic updates over server data
            return {
              ...serverItem,
              user_data: {
                ...serverItem.user_data,
                ...existingItem.user_data,
              },
            };
          }


          return serverItem;
        });
      }

      state.lesson.data = serverData;
    });

    builder.addCase(getLesson.rejected, (state, action) => {
      if (action.meta.aborted) {
        state.lesson.loading = "idle";
        return;
      }

      state.lesson.loading = "failed";
      state.lesson.error = (action.payload as string) || "Failed to load lesson";
      console.error("Failed to load lesson:", action.error?.message || action.payload);
    });

    // Handle updateVideoProgress actions
    builder.addCase(updateVideoProgress.pending, state => {
      state.videoProgress.loading = "pending";
      state.videoProgress.error = null;
    });

    builder.addCase(updateVideoProgress.fulfilled, (state, action) => {
      state.videoProgress.loading = "succeeded";

      // The optimistic update has already been applied, no need to update again
    });

    builder.addCase(updateVideoProgress.rejected, (state, action) => {
      state.videoProgress.loading = "failed";
      state.videoProgress.error = (action.payload as string) || "Failed to update video progress";

      // TODO: Revert optimistic update on error
    });

    // Handle getTeamProgress actions
    builder.addCase(getTeamProgress.pending, state => {
      state.teamProgress.loading = "pending";
      state.teamProgress.error = null;
    });

    builder.addCase(getTeamProgress.fulfilled, (state, action) => {
      state.teamProgress.loading = "succeeded";
      state.teamProgress.data = action.payload.data;
    });

    builder.addCase(getTeamProgress.rejected, (state, action) => {
      state.teamProgress.loading = "failed";
      state.teamProgress.error = (action.payload as string) || "Failed to load team progress";
    });
  },
});

export const getCourses = createAsyncThunk<{ data: any[]; total: number }, void, { rejectValue: string }>(
  "elearning/fetch_courses",
  async (_, { rejectWithValue }) => {
    const response = await doGetElearningCourses();

    // If response is null, it means a 500 error occurred
    if (response === null || response === undefined) {
      return rejectWithValue("Failed to load courses");
    }

    // Ensure response is an array
    const courses = Array.isArray(response) ? response : [];

    return {
      data: courses,
      total: courses.length,
    };
  }
);

export const getLesson = createAsyncThunk<{ data: any }, GetLessonParams, { rejectValue: string }>(
  "elearning/fetch_lesson",
  async ({ id, lang, lessionId }, { rejectWithValue }) => {
    try {
      const response = await doGetElearningCourseLessonById(id, lang, lessionId);


      return { data: response };
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to fetch lesson";


      return rejectWithValue(error);
    }
  }
);

export const updateVideoProgress = createAsyncThunk<{ data: any }, UpdateVideoProgressParams, { rejectValue: string }>(
  "elearning/update_video_progress",
  async ({ courseId, lessonId, dataId, time, completedAt, lang, progress }, { rejectWithValue, dispatch }) => {
    try {
      const response = await doUpdateVideoProgress(courseId, lessonId, dataId, time, completedAt, progress);


      // If we cleared completion OR just marked as completed, refetch the lesson to sync live data from server
      if ((completedAt === "" || (typeof completedAt === "string" && completedAt.length > 0)) && lang) {
        // @ts-ignore dispatch typed within thunk
        await dispatch(getLesson({ id: courseId, lang, lessionId: lessonId }));
      }


      return { data: response };
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to update video progress";

      console.error("Error updating video progress:", error);

      return rejectWithValue(error);
    }
  }
);

export const getTeamProgress = createAsyncThunk<{ data: any }, { courseId: string }, { rejectValue: string }>(
  "elearning/fetch_team_progress",
  async ({ courseId }, { rejectWithValue }) => {
    try {
      const response = await doGetElearningCourseTeamProgress(courseId);


      return { data: response };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveQuestionAnswers = createAsyncThunk<{ data: any }, SaveQuestionAnswersParams, { rejectValue: string }>(
  "elearning/save_question_answers",
  async ({ courseId, lessonId, answers, lang, refetch }, { rejectWithValue, dispatch }) => {
    try {
      const response = await doSaveElearningQuestionAnswers(courseId, lessonId, answers);

      if (refetch && lang) {
        // @ts-ignore typed within thunk
        await dispatch(getLesson({ id: courseId, lang, lessionId: lessonId }));
      }


      return { data: response };
    } catch (err) {
      const error = err instanceof Error ? err.message : "Failed to save question answers";


      return rejectWithValue(error);
    }
  }
);

export const {
  clearLesson,
  updateVideoProgressOptimistic,
  clearVideoProgressError,
  updateQuestionAnswersOptimistic,
  cacheQuestionAnswers,
  clearAnswersCache,
  filterCourses,
} = elearningSlice.actions;

export default elearningSlice.reducer;
